import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HashNavigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import CreditsBalanceHeader from '../../components/CreditsBalanceHeader';
import Icon, { Icons } from '../../components/Icon/Icon';
import AppConfig from '../../config';
import useBackButton from '../../hooks/useBackButton';
import RequestService from '../../services/RequestService';
import StorageService from '../../services/StorageService';
import WebSocketService from '../../services/WebSocketService';
import AccountTemplate from './components/AccountTemplate';

import 'swiper/css';
import './Account.scss';

const { routes } = AppConfig;

const SLIDES_PER_VIEW = 2;
const maxCardTranslate = ((SLIDES_PER_VIEW - 1) / 2) * 100;

const getCardTranslateSizes = (progress = 0, slidesGrid = []) => {
	return slidesGrid.map((_, index) => {
		const slideCardTranslate = (progress * (slidesGrid.length - 1) - index) * maxCardTranslate;
		return Math.max(-maxCardTranslate, Math.min(slideCardTranslate, maxCardTranslate));
	});
};

const Account = () => {
	const [searchParams] = useSearchParams();
	const [cardTranslateSizes, setCardTranslateSizes] = useState([]);
	const [accounts, setAccounts] = useState([]);

	const asset = searchParams.get('asset');

	const currencies = useSelector(store => store.app.currencies);
	const activeCurrency = currencies.find(i => i.Symbol === asset);

	const onProgress = (progressData, progress) => {
		setCardTranslateSizes(getCardTranslateSizes(progress, progressData?.slidesGrid));
	};

	const GetAddressesByCurrency = useCallback(async () => {
		const res = await RequestService.GetAddressesByCurrency(activeCurrency?.Id);

		setAccounts(res?.Addresses);
	}, []);

	useEffect(() => {
		GetAddressesByCurrency();

		const refreshAddressesByCurrency = data => {
			if (data?.type === 'MobileTransactionUpdatedMsg' && data?.msg?.transactionStatus === 2) GetAddressesByCurrency();
		};
		WebSocketService.AddCustomHandler(refreshAddressesByCurrency);
		return () => {
			WebSocketService.RemoveCustomeHandler('refreshAddressesByCurrency');
		};
	}, [GetAddressesByCurrency, currencies]);

	useEffect(() => {
		StorageService.UpdateBillingBalance();
	}, []);

	const navigate = useNavigate();
	useBackButton(true);

	const goBillingPage = () => {
		navigate({ pathname: routes.billingHistory });
	};

	return (
		<div className='Account'>
			<CreditsBalanceHeader topUpAction={goBillingPage} />

			{accounts?.length > 0 ? (
				<Swiper
					className='Account__swiper'
					onProgress={onProgress}
					watchSlidesProgress={true}
					hashNavigation={{ watchState: true, replaceState: true }}
					modules={[HashNavigation]}
					touchStartPreventDefault={false}
				>
					<Icon className='Account-bg' type={Icons.AssetHeaderBG} />
					{accounts.map((account, index) => (
						<SwiperSlide data-hash={account.AddressId} key={account.AddressId}>
							<AccountTemplate
								account={account}
								activeCurrency={activeCurrency}
								cardTranslate={cardTranslateSizes[index]}
								headerTransition={!(cardTranslateSizes[index] % maxCardTranslate)}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			) : null}
		</div>
	);
};

export default Account;
